import { cn } from '@workspace/lib/utils';
import { buildDatUrl, buildSrcSet, resolveAlt, resolveSource } from '@workspace/lib/picture.utils';
import type { PictureSource } from '@workspace/lib/picture.utils';

export interface PictureProps {
  image?: PictureSource;
  src?: string;
  alt?: string;
  desktopWidth: number;
  desktopHeight: number;
  tabletWidth?: number;
  tabletHeight?: number;
  tabletBreakpoint?: number;
  mobileWidth?: number;
  mobileHeight?: number;
  mobileBreakpoint?: number;
  className?: string;
  imgClassName?: string;
  draggable?: boolean;
  ariaHidden?: boolean;
  priority?: boolean;
  loading?: 'eager' | 'lazy';
  fill?: boolean;
  objectPosition?: string;
}

export function Picture({
  image,
  src,
  alt,
  desktopWidth,
  desktopHeight,
  tabletWidth,
  tabletHeight,
  tabletBreakpoint = 1280,
  mobileWidth,
  mobileHeight,
  mobileBreakpoint = 768,
  className,
  imgClassName,
  draggable,
  ariaHidden,
  priority = false,
  loading,
  fill = false,
  objectPosition,
}: PictureProps) {
  const resolved = image ? resolveSource(image) : undefined;
  const baseUrl = resolved?.baseUrl || src || '';
  const shouldTransform = !!resolved?.isBynder && !resolved.isSvg && !!resolved.baseUrl;

  if (!baseUrl) return null;

  const effectiveMobileWidth = mobileWidth ?? desktopWidth;
  const effectiveMobileHeight = mobileHeight ?? desktopHeight;
  const hasTabletSource = !!(tabletWidth && tabletHeight);
  const hasMobileSource =
    hasTabletSource || effectiveMobileWidth !== desktopWidth || effectiveMobileHeight !== desktopHeight;

  // Plain 1x URL for the `src` attribute fallback
  const imgW = hasMobileSource ? effectiveMobileWidth : desktopWidth;
  const imgH = hasMobileSource ? effectiveMobileHeight : desktopHeight;
  const imgSrc = shouldTransform ? buildDatUrl(baseUrl, imgW, imgH) : baseUrl;
  const imgWidth = imgW;
  const imgHeight = imgH;

  return (
    <picture className={cn(fill && 'absolute inset-0 size-full', className)}>
      {hasMobileSource && (
        <source
          media={`(min-width: ${hasTabletSource ? tabletBreakpoint : mobileBreakpoint}px)`}
          srcSet={buildSrcSet(baseUrl, desktopWidth, desktopHeight, shouldTransform)}
          width={desktopWidth}
          height={desktopHeight}
        />
      )}
      {hasTabletSource && (
        <source
          media={`(min-width: ${mobileBreakpoint}px)`}
          srcSet={buildSrcSet(baseUrl, tabletWidth!, tabletHeight!, shouldTransform)}
          width={tabletWidth!}
          height={tabletHeight!}
        />
      )}
      <img
        src={imgSrc}
        srcSet={shouldTransform ? buildSrcSet(baseUrl, imgWidth, imgHeight, shouldTransform) : undefined}
        alt={image ? resolveAlt(image, alt) : (alt ?? '')}
        width={imgWidth}
        height={imgHeight}
        loading={priority ? 'eager' : (loading ?? 'lazy')}
        fetchPriority={priority ? 'high' : undefined}
        decoding="async"
        aria-hidden={ariaHidden}
        draggable={draggable}
        className={cn('w-full h-full object-cover', imgClassName)}
        style={objectPosition ? { objectPosition } : undefined}
      />
    </picture>
  );
}
